"use client";

import artworkApi from "@/api/artwork";
import templateApi from "@/api/template";
import AutoComplete from "@/components/common/AutoComplete";
import { S3_CDN } from "@/constants/API";
import EnhancedTable from "@/hocs/EnhancedTable";
import { Artwork } from "@/interfaces/artwork.interface";
import { Template } from "@/interfaces/template.interface";
import useTimezoneStore from "@/stores/timezone.store";
import { useToastStore } from "@/stores/toast.store";
import { formatDateWithTimezone } from "@/utils/func";
import { Modal } from "@mui/material";
import Link from "next/link";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { useClickOutside } from "primereact/hooks";
import { Image } from "primereact/image";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RefObject, useEffect, useRef, useState } from "react";

const ROWS_PER_PAGE = 10;
const TemplatesList = () => {
  const refetchRef = useRef<(() => void) | null>(null);
  const displayTimeZone = useTimezoneStore((s) => s.timezone);
  const [selectTemplateId, setSelectTemplateId] = useState<string | null>(null);
  const handleDeleteTemplate = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string,
    name: string,
  ) => {
    setSelectTemplateId(id);
    confirmPopup({
      target: event.currentTarget,
      message: (
        <>
          Are you sure you want to delete the template
          <p className="max-w-2xl">
            <strong>&quot;{name}&quot;?</strong>
          </p>
        </>
      ),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        templateApi.deleteOne(id).then(() => {
          useToastStore
            .getState()
            .showToast("success", "success", "Template deleted successfully");
          refetchRef.current?.();
        });
      },
      reject: () => {},
      onHide: () => setSelectTemplateId(null),
    });
  };

  return (
    <div className="flex h-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <EnhancedTable
        cachedKey="templates"
        fetchData={(params) => templateApi.getMany({ ...params, view: "list" })}
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search templates"
        title="Templates"
        refetchRef={refetchRef}
        rowClassName={(rowData) => {
          return rowData._id === selectTemplateId ? "highlight-table-row" : "";
        }}
      >
        <Column
          header="Thumbnail"
          key={"thumbnail"}
          body={(rowData: Template) => (
            <Image
              crossOrigin="anonymous"
              src={
                rowData?.thumbnailPath
                  ? `${S3_CDN}/${rowData?.thumbnailPath}`
                  : "/images/no_image.png"
              }
              alt={rowData.name}
              width="64"
              height="64"
              className="h-16 w-16 rounded object-cover"
              preview
            />
          )}
          style={{ width: "70px" }}
        />

        <Column
          field="name"
          key={"name"}
          header="Template Name"
          rowSpan={2}
          body={(rowData: Template) => (
            <RenderTemplateName template={rowData} />
          )}
        />
        <Column
          header="Timestamp"
          field="createdAt"
          body={(rowData: Template) => {
            return (
              <p>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.createdAt, displayTimeZone)}
                </span>
                <span className="flex h-max w-full items-center justify-center">
                  <i
                    className="pi pi-arrow-down text-sm text-gray-500"
                    style={{
                      fontSize: "0.5rem",
                    }}
                  ></i>
                </span>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.updatedAt, displayTimeZone)}
                </span>
              </p>
            );
          }}
          style={{ width: "70px", whiteSpace: "nowrap", textAlign: "start" }}
        />
        <Column
          header="Actions"
          body={(template: Template) => {
            return (
              <div className="flex items-center gap-1">
                <CloneTemplateButton
                  template={template}
                  refetchRef={refetchRef}
                />
                <Link
                  href={`/artworks/${template.artworkId}?templateId=${template._id}`}
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                >
                  <i
                    className="pi pi-pen-to-square text-green-600"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </Link>
                <span
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                  onClick={(event) =>
                    handleDeleteTemplate(event, template._id, template.name)
                  }
                >
                  <i
                    className="pi pi-trash text-red-600"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </span>
              </div>
            );
          }}
          style={{ width: "120px" }}
        />
      </EnhancedTable>
    </div>
  );
};

export default TemplatesList;

const RenderTemplateName = ({ template }: { template: Template }) => {
  const [isEditing, setIsEditing] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [templateName, setTemplateName] = useState("");
  useEffect(() => {
    setTemplateName(template.name);
  }, [template]);
  const handleClose = () => {
    setIsEditing(false);
    setTemplateName(template.name);
  };
  const handleSave = () => {
    templateApi
      .updateOneById(template._id, {
        name: templateName,
      })
      .then(() => {
        useToastStore
          .getState()
          .showToast(
            "success",
            "success",
            "Template name updated successfully",
          );
        template.name = templateName;
        setTemplateName(templateName);
        setIsEditing(false);
      });
  };
  useClickOutside(templateRef as RefObject<Element>, handleClose);
  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
      const length = textAreaRef.current.value.length;
      textAreaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);
  return (
    <div
      className="relative w-full rounded p-2 transition-all hover:bg-gray-100"
      ref={templateRef}
    >
      {isEditing ? (
        <InputTextarea
          rows={3}
          ref={textAreaRef}
          cols={30}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="w-full rounded border p-1"
        />
      ) : (
        <span
          className="line-clamp-2 w-full cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          {templateName}
        </span>
      )}
      <div
        className={`absolute right-1 bottom-1 gap-2 ${
          isEditing ? "flex" : "hidden"
        }`}
      >
        <i
          className="pi pi-undo cursor-pointer rounded-md bg-white p-2 text-black shadow hover:bg-gray-600 hover:text-white"
          onClick={handleClose}
        ></i>
        <i
          className="pi pi-save cursor-pointer rounded-md bg-white p-2 text-blue-600 shadow hover:bg-blue-600 hover:text-white"
          onClick={handleSave}
        ></i>
      </div>
    </div>
  );
};

function CloneTemplateButton({
  template,
  refetchRef,
}: {
  template: Template;
  refetchRef: React.RefObject<(() => void) | null>;
}) {
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openCreateNew, setOpenCreateNew] = useState(false);
  const onCloneTemplate = async (artworkId: string, templateId: string) => {
    return await templateApi
      .cloneTemplate(artworkId, templateId)
      .then((res) => {
        refetchRef.current?.();
        useToastStore
          .getState()
          .showToast("success", "success", "Template cloned successfully");
      });
  };
  const onCreateNewArtwork = async (name: string) => {
    return await artworkApi
      .createOne({ name, createTemplateDefault: false })
      .then((res) => {
        return res;
      });
  };
  const accept = async () => {
    if (!selectedArtwork) {
      useToastStore
        .getState()
        .showToast("error", "error", "Please select an artwork");
      return;
    }
    setLoading(true);
    let artworkToClone = selectedArtwork;
    if (openCreateNew) {
      artworkToClone = await onCreateNewArtwork(selectedArtwork.name);
    }
    await onCloneTemplate(artworkToClone._id, template._id).then(() => {
      setOpen(false);
      setLoading(false);
      setSelectedArtwork(null);
      setOpenCreateNew(false);
    });
  };

  return (
    <>
      <span
        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
        onClick={(e) => setOpen(true)}
      >
        <i
          className="pi pi-copy text-blue-600"
          style={{
            fontSize: "1.2rem",
          }}
        ></i>
      </span>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedArtwork(null);
        }}
        className="flex items-center justify-center"
      >
        <div className="w-3xl rounded-md bg-white p-4">
          <div className="mt-2 mb-2 flex w-full items-center justify-between gap-2">
            {openCreateNew ? (
              <label htmlFor="" className="text-base font-semibold">
                Create the target artwork
              </label>
            ) : (
              <label htmlFor="" className="text-base font-semibold">
                Select the target artwork
              </label>
            )}

            {openCreateNew ? (
              <button
                className="border-primary-50 text-primary-50 flex h-max w-max flex-none cursor-pointer items-center justify-center rounded-md border bg-white p-2 text-center text-xs font-semibold hover:shadow-md"
                onClick={() => {
                  setOpenCreateNew(false);
                  setSelectedArtwork(null);
                }}
              >
                Select existed artwork
              </button>
            ) : (
              <button
                className="bg-primary-50 flex h-max w-max flex-none cursor-pointer items-center justify-center rounded-md border border-gray-300 p-2 text-center text-xs font-semibold text-white hover:shadow-md"
                onClick={() => {
                  setOpenCreateNew(true);
                  setSelectedArtwork(null);
                }}
              >
                Create new artwork
              </button>
            )}
          </div>
          {openCreateNew ? (
            <div className="mt-2 flex w-full flex-col gap-2">
              <InputText
                value={selectedArtwork ? selectedArtwork.name : ""}
                onChange={(e) => {
                  if (e.target.value.trim() === "") {
                    setSelectedArtwork(null);
                    return;
                  }
                  setSelectedArtwork({
                    name: e.target.value,
                  } as Artwork);
                }}
                placeholder="Input new artwork name"
                className="w-full rounded border p-1"
              />
            </div>
          ) : (
            <AutoComplete
              value={selectedArtwork}
              onChange={(e) => {
                setSelectedArtwork(e as Artwork);
              }}
              className="w-full"
              funcCall={({ search, size, page }) =>
                artworkApi.getMany({ search: search, size: 20, page: 1 })
              }
            />
          )}
          <div className="flex w-full items-center justify-end gap-4 pt-4">
            <span
              className="text-md flex w-max cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-gray-200 p-2 font-semibold hover:bg-gray-300"
              onClick={() => {
                setSelectedArtwork(null);
                setOpen(false);
              }}
            >
              Cancel
            </span>
            <span
              className={`border-primary-50 bg-primary-50 text-md flex w-max cursor-pointer items-center justify-center gap-2 rounded-md border p-2 font-semibold text-white hover:opacity-90 ${!selectedArtwork && "cursor-not-allowed opacity-50"}`}
              onClick={() => {
                accept();
              }}
            >
              Clone {loading && <i className="pi pi-spin pi-spinner"></i>}
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
}
